import { useIntl } from 'react-intl';
import {
  IconCircleMinus,
  IconCirclePlus,
  IconEye,
  IconEyeOff,
  IconGripVertical,
} from '@rossum/ui/icons/tabler';
import {
  buttonClasses,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { DragHandleProps } from '../../decorators/SortableWrapper/SortableWrapper';

type Column = {
  columnType: 'schema' | 'meta';
  visible?: boolean;
  headerName?: string;
  label?: string;
};

type Props = {
  onRemoveColumn?: () => void;
  onToggleVisiblity?: () => void;
  onAddColumn?: () => void;
  column: Column;
  isDraggable?: boolean;
  dragHandleProps?: DragHandleProps;
  isOverlay?: boolean;
};

export const ColumnItem = ({
  onRemoveColumn,
  onAddColumn,
  onToggleVisiblity,
  column,
  isDraggable = false,
  dragHandleProps,
  isOverlay,
}: Props) => {
  const intl = useIntl();
  const { label, visible, headerName } = column;
  const { isDragging, attributes, listeners } = dragHandleProps ?? {};

  return (
    <Stack
      direction="row"
      component={Paper}
      elevation={isOverlay ? 4 : 0}
      sx={{
        px: 1,
        color: 'text.secondary',
        opacity: isDragging ? 0.3 : 1,
        ...(isOverlay ? {} : { boxShadow: 'none' }),
      }}
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      pr={1}
    >
      {isDraggable ? (
        <IconButton
          disableRipple
          {...(listeners ?? {})}
          {...(attributes ?? {})}
          sx={{
            [`&.${buttonClasses.focusVisible}`]: {
              backgroundColor: t => t.palette.background.default,
            },
            color: 'inherit',
            cursor: isOverlay ? 'grabbing' : 'grab',
            outline: 'none',
          }}
        >
          <SvgIcon fontSize="medium">
            <IconGripVertical />
          </SvgIcon>
        </IconButton>
      ) : null}
      {onToggleVisiblity ? (
        <IconButton onClick={onToggleVisiblity}>
          <SvgIcon fontSize="large" color={visible ? 'success' : 'secondary'}>
            {visible ? <IconEye /> : <IconEyeOff />}
          </SvgIcon>
        </IconButton>
      ) : null}
      <Stack flexGrow={1}>
        <Typography
          variant="body1"
          sx={{
            overflowWrap: 'anywhere',
            color: visible ? 'text.primary' : 'text.secondary',
          }}
        >
          {headerName ??
            intl.formatMessage({
              id: 'containers.allDocuments.columnsPanel.columnNameMissing',
            })}
        </Typography>
        {label ? <Typography variant="body2">{label}</Typography> : null}
      </Stack>
      {onRemoveColumn ? (
        <IconButton onClick={onRemoveColumn}>
          <SvgIcon fontSize="large" color="secondary">
            <IconCircleMinus />
          </SvgIcon>
        </IconButton>
      ) : null}
      {!onRemoveColumn && onAddColumn ? (
        <IconButton onClick={onAddColumn}>
          <SvgIcon fontSize="large" color="secondary">
            <IconCirclePlus />
          </SvgIcon>
        </IconButton>
      ) : null}
    </Stack>
  );
};
